import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { cn } from '@/lib/utils';

interface TicketDistribution {
  name: string;
  value: number;
  color: string;
  [key: string]: string | number;
}

interface TicketDistributionChartProps {
  data: TicketDistribution[];
  title?: string;
  onSegmentClick?: (name: string, value: number) => void;
  activeSegment?: string | null;
}

export function TicketDistributionChart({
  data,
  title = "Ticket Distribution",
  onSegmentClick,
  activeSegment
}: TicketDistributionChartProps) {
  // Filter out zero values for cleaner chart
  const chartData = data.filter(item => item.value > 0);
  const isClickable = !!onSegmentClick;

  interface TooltipPayloadEntry {
    name: string;
    value: number;
  }

  interface CustomTooltipProps {
    active?: boolean;
    payload?: TooltipPayloadEntry[];
  }

  const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
          <p className="font-semibold text-gray-900 dark:text-gray-50">
            {payload[0].name}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {payload[0].value} ticket{payload[0].value !== 1 ? 's' : ''}
          </p>
        </div>
      );
    }
    return null;
  };

  interface LegendPayloadEntry {
    value: string;
    color: string;
    payload?: {
      value: number;
    };
  }

  interface CustomLegendProps {
    payload?: LegendPayloadEntry[];
  }

  const CustomLegend = ({ payload }: CustomLegendProps) => {
    if (!payload) return null;
    return (
      <div className="flex flex-wrap justify-center gap-3 mt-4">
        {payload.map((entry: LegendPayloadEntry, index: number) => {
          const isActive = activeSegment === entry.value;
          return (
            <div
              key={`legend-${index}`}
              className={cn(
                "flex items-center gap-1.5 px-2 py-1 rounded transition-all",
                isClickable && "cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800",
                isActive && "bg-gray-100 dark:bg-gray-800 ring-1 ring-gray-300 dark:ring-gray-600"
              )}
              onClick={() => {
                if (isClickable && entry.payload) {
                  onSegmentClick?.(entry.value, entry.payload.value);
                }
              }}
            >
              <div
                className="w-3 h-3 rounded-sm"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                {entry.value}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Card className="col-span-full lg:col-span-1">
      <CardHeader className="pb-4">
        <CardTitle className="text-base font-semibold">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <div className="h-[240px] flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
            No ticket data available
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={240} minHeight={240}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
                animationBegin={0}
                animationDuration={800}
                onClick={(data) => {
                  if (isClickable && data) {
                    onSegmentClick?.(data.name, data.value);
                  }
                }}
                style={isClickable ? { cursor: 'pointer' } : undefined}
              >
                {chartData.map((entry, index) => {
                  const isActive = activeSegment === entry.name;
                  return (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.color}
                      stroke={isActive ? '#fff' : 'transparent'}
                      strokeWidth={isActive ? 3 : 0}
                      style={{
                        filter: isActive ? 'drop-shadow(0 0 4px rgba(0,0,0,0.3))' : undefined,
                        transform: isActive ? 'scale(1.05)' : undefined,
                        transformOrigin: 'center',
                      }}
                    />
                  );
                })}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend content={<CustomLegend />} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
